/**
 * Which opening animation the gate-open overlay plays — device-local, exactly like
 * `colorScheme.ts` beside it and for the same reason: it is a property of *this handset*, there
 * is no account field for it, and minting one is a PRD decision rather than a screen's.
 *
 * It lives in `theme/` rather than in `screens/home/` because the pill that writes it is on the
 * preferences screen (P6) and the overlay that reads it is on Home (P7); the value itself is
 * appearance, which is `theme/`'s subject.
 *
 * **`check` is the default.** `gate` is the shipped cloud-at-the-gate overlay
 * (`screens/home/GateOpenOverlay.tsx`), kept selectable rather than deleted.
 *
 * **A style is a surface, not a value in this union (D215) — and D218 is that same rule spent
 * from the other end.** D198 (c) decided the mechanism below (the key, the default, the
 * unrecognised-value fallback, the pill) and a further style re-decides none of it; D198 (b)
 * decided a *drawing* clause by clause, and a further style re-opens all of that, so each one is
 * minted on its own. **Which is why withdrawing one is a decision too**: D215's `car` went in
 * full — component, art, stylesheet block, label and glyph — rather than being left unreachable
 * behind a shortened union, because a surface nothing renders is not a smaller surface, it is
 * dead weight wearing the shape of an option. Adding a member here is the cheapest part of
 * shipping one, which is exactly what makes a full-screen surface look like an enum entry.
 *
 * **A shortened union is a live migration, and `isStyle` is the whole of it.** A resident who had
 * chosen `car` still has that string under this key; it is simply unrecognised now, so it falls
 * back to `DEFAULT_OPEN_ANIMATION` on the next read exactly as any other stray value does, and
 * the pill re-stores a live one on the next tap. Nothing migrates the key, and nothing needs to.
 */

import { STAGE } from '../auth/config'

export type OpenAnimationStyle = 'check' | 'gate'

export const DEFAULT_OPEN_ANIMATION: OpenAnimationStyle = 'check'

/**
 * The order the preferences pill lays them out: the default first, then the shipped one, then
 * each later arrival appended in the order it landed.
 */
export const OPEN_ANIMATION_OPTIONS: readonly OpenAnimationStyle[] = ['check', 'gate']

const animationKey = (stage: string): string => `selogate.${stage}.open-animation`

function isStyle(value: unknown): value is OpenAnimationStyle {
  return value === 'check' || value === 'gate'
}

/**
 * The stored style, or the default. **Never throws** — a private-mode browser throws on
 * `getItem` rather than returning `null` (`auth/credentials.ts`'s rule), and an animation
 * preference is a convenience whose absence costs nothing.
 */
export function readOpenAnimation(stage: string = STAGE): OpenAnimationStyle {
  try {
    const raw = globalThis.localStorage?.getItem(animationKey(stage))
    return isStyle(raw) ? raw : DEFAULT_OPEN_ANIMATION
  } catch {
    return DEFAULT_OPEN_ANIMATION
  }
}

export function writeOpenAnimation(next: OpenAnimationStyle, stage: string = STAGE): void {
  try {
    globalThis.localStorage?.setItem(animationKey(stage), next)
  } catch {
    /* ignore — see readOpenAnimation */
  }
}
